import { UseFormReturn } from 'react-hook-form';
import {
    ACCEPTED_IMAGE_TYPES,
    ACCEPTED_VIDEO_TYPES,
    FileObject,
    MAX_IMAGE_SIZE,
    MAX_VIDEO_SIZE,
    StarStoryFormData,
    StarStoryFormSchema,
} from '@/components/blocks/StarStoryForm/StarStoryFormTabContent';
import { CustomCheckbox } from '@/components/elements/CustomCheckbox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/elements/Form';
import { Input } from '@/components/elements/Input';
import { Textarea } from '@/components/elements/TextArea/TextArea';
import { FormTooltip } from '@/components/elements/Tooltip/ToolTip';
import classNames from '@/lib/classNames';
import { z } from 'zod';
import FileInput from '../FileInput';

interface TabFieldsProps {
    form: UseFormReturn<StarStoryFormData>;
    fieldName: keyof typeof StarStoryFormSchema.shape;
    inputConfig: {
        label: string;
        placeholder?: string;
    };
    className?: string;
    inputType?: 'text' | 'textarea';
    files?: FileObject[];
}

function TabFields({ form, fieldName, inputConfig, className, inputType }: TabFieldsProps) {
    const { errors } = form.formState;
    return (
        <FormField
            key={fieldName}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
                <FormItem className={classNames(className, 'space-y-2 padding-x-2 mb-4 sm:mb-6')}>
                    <FormLabel>
                        <span>
                            {inputConfig.label}{' '}
                            {!(StarStoryFormSchema.shape[fieldName] instanceof z.ZodOptional) &&
                                (!errors[fieldName] ? <span>*</span> : <FormTooltip type="alert" content="Required Field" />)}
                        </span>
                    </FormLabel>
                    <FormControl>
                        {inputType === 'textarea' ? (
                            <Textarea
                                {...field}
                                value={field.value as string}
                                placeholder={inputConfig?.placeholder}
                                className={classNames('border-primary', errors[fieldName] && 'border-destructive')}
                            />
                        ) : (
                            <Input
                                {...field}
                                value={field.value as string}
                                required={!(StarStoryFormSchema.shape[fieldName] instanceof z.ZodOptional)}
                                className={classNames('border-primary', errors[fieldName] && 'border-destructive')}
                            />
                        )}
                    </FormControl>
                    <FormMessage className="mt-1" />
                </FormItem>
            )}
        />
    );
}

function UploadTabFields({ form, fieldName, inputConfig }: TabFieldsProps) {
    const { errors } = form.formState;

    return (
        <FormField
            key={fieldName}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
                <FormItem className={` ${inputConfig.label !== 'Image' && inputConfig.label !== 'Video' ? 'flex items-center mb-2' : ''}`}>
                    <FormControl className={` ${inputConfig.label !== 'Image' && inputConfig.label !== 'Video' ? 'flex-1 ml-2' : ''}`}>
                        {inputConfig.label === 'Image' ? (
                            <FileInput
                                {...field}
                                form={form}
                                contentType={inputConfig.label}
                                maxSize="Max photo size: 10mb per image"
                                maxFileSize={MAX_IMAGE_SIZE}
                                acceptedFormats={ACCEPTED_IMAGE_TYPES}
                                acceptedFormatsText="png, jpg, heic"
                            />
                        ) : inputConfig.label === 'Video' ? (
                            <FileInput
                                {...field}
                                form={form}
                                contentType={inputConfig.label}
                                maxSize="Max video size: 320mb"
                                maxFileSize={MAX_VIDEO_SIZE}
                                acceptedFormats={ACCEPTED_VIDEO_TYPES}
                                acceptedFormatsText="mp4, avi, mov"
                            />
                        ) : (
                            <CustomCheckbox {...field} />
                        )}
                    </FormControl>
                    <FormLabel>
                        <span className="pr-1">{inputConfig.label !== 'Image' && inputConfig.label !== 'Video' && inputConfig.label}</span>
                        {!(StarStoryFormSchema.shape[fieldName] instanceof z.ZodOptional) &&
                            (!errors[fieldName] ? (
                                <span className={classNames('w-4 h-4', errors[fieldName] && 'text-red-600')}>*</span>
                            ) : (
                                <FormTooltip type="alert" content="Required Field" />
                            ))}
                    </FormLabel>
                </FormItem>
            )}
        />
    );
}

export { TabFields, UploadTabFields };
